/**
 * useWorkflowRecordRealtime - Tempo real do processo (Workflow 2.0) por registro
 *
 * Padrão de SaaS de grande porte: a assinatura é por REGISTRO (ex.: o negócio
 * aberto), nunca por tenant. O canal escuta apenas `workflow_instances` com
 * filtro `root_entity_id = <id do registro>`, de modo que eventos de outros
 * negócios ou de outros tenants nem chegam ao cliente (o servidor do realtime
 * aplica o filtro e a RLS).
 *
 * A cada evento da instância (nascimento, avanço de etapa, cancelamento ou
 * saída pela regra), invalida:
 *   - ['deal-workflow', ...]        -> trilha/etapa atual (useWorkflowInstanceTimeline)
 *   - ['workflow-human-tasks']      -> pendências/aprovações (useWorkflowHumanTasks)
 *   - ['workflow-guard-check']      -> decisão da trava (useWorkflowGuardCheck),
 *                                      liberando botões de proposta/contrato sem F5
 *
 * A tela do negócio NÃO assina `workflow_human_tasks` diretamente: como essa
 * tabela não carrega root_entity_id, o filtro só poderia ser por tenant,
 * gerando tráfego desnecessário — os eventos da instância já bastam como sinal.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-06 v1.2.0 - Nome do canal recebe sufixo único por instância,
 *            corrigindo erro ao abrir negócio dentro de negócio (canal duplicado).
 *   RELEASE: 2026-09-04 v1.1.0 - Passa a invalidar também 'workflow-guard-check'
 *            (trava exibida nos botões de proposta/contrato/financeiro). O ouvinte
 *            foi elevado da aba de fluxos para o modal do negócio
 *            (DealDetailsModalPremium), cobrindo todas as abas.
 *   RELEASE: 2026-09-01 v1.0.0 - Criação inicial (atualização imediata da aba
 *            Resumo do negócio sem F5).
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useWorkflowRecordRealtime');

interface Params {
  /** Id da entidade-raiz (ex.: o negócio aberto) */
  rootEntityId?: string | null;
  /** Habilita a assinatura (módulo Workflow ligado) */
  enabled?: boolean;
}

export function useWorkflowRecordRealtime({ rootEntityId, enabled = true }: Params) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!tenantId || !rootEntityId || !enabled) return;

    // Assinatura filtrada por registro: somente eventos desta entidade chegam ao cliente.
    // v1.2.0: sufixo único por instância evita reaproveitar canal já assinado
    // quando o mesmo negócio é aberto em modais empilhados.
    const instanceSuffix = Math.random().toString(36).slice(2, 10);
    const channel = supabase
      .channel(`workflow-record-${rootEntityId}-${instanceSuffix}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workflow_instances',
          filter: `root_entity_id=eq.${rootEntityId}`,
        },
        (payload) => {
          logger.debug('Evento do processo recebido', {
            evento: payload.eventType,
            rootEntityId,
          });
          // Trilha/etapa atual do registro
          queryClient.invalidateQueries({ queryKey: ['deal-workflow', rootEntityId, tenantId] });
          // Pendências/aprovações que seguram o avanço
          queryClient.invalidateQueries({ queryKey: ['workflow-human-tasks'] });
          // v1.1.0: decisão da trava exibida nos botões (proposta/contrato/cobrança)
          queryClient.invalidateQueries({ queryKey: ['workflow-guard-check'] });
        },
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          logger.warn('Falha na assinatura do processo', { rootEntityId });
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [rootEntityId, tenantId, enabled, queryClient]);
}
